import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Share2, Wallet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fetchAffiliateMe, fetchReferralLinks, referralLinkFor, type AffiliateMe, type ReferralLinks } from '@/services/affiliateApi';

/**
 * Refer a friend: the member's own referral links, shown where they are most likely
 * to share. After registering for an event (`productId`), the one link for that
 * event. On the account page, the upcoming events, each with its own link, and the
 * membership: never a bare homepage link, which gives a friend nothing to act on
 * (Joe, Oct 16 2026). `showBalance` adds the Hey Joe credit tile (account page).
 * Renders nothing for non-members (no code) unless they hold credit.
 */
const whenLine = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
const priceLine = (e: { priceCents: number; freeForMembers: boolean; membersOnly: boolean }) =>
  e.membersOnly ? 'Members only' : e.priceCents <= 0 ? 'Free' : `$${(e.priceCents / 100).toFixed(e.priceCents % 100 ? 2 : 0)}${e.freeForMembers ? ', free for members' : ''}`;

function useCopyShare() {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(link);
      setTimeout(() => setCopied(c => (c === link ? null : c)), 2000);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the link and copy it manually.', variant: 'destructive' });
    }
  };
  const share = async (link: string, title: string) => {
    const nav = navigator as Navigator & { share?: (d: { title?: string; url?: string }) => Promise<void> };
    if (nav.share) {
      try { await nav.share({ title, url: link }); return; } catch { /* dismissed */ }
    }
    copy(link);
  };
  return { copied, copy, share };
}

const pillYellow: React.CSSProperties = { padding: '8px 13px', borderRadius: 999, background: 'var(--hj-yellow)', border: 'none', color: '#111', fontSize: 13, cursor: 'pointer', minHeight: 36 };
const pillLine: React.CSSProperties = { padding: '8px 13px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-ink)', color: 'var(--hj-beige-ink)', fontSize: 13, cursor: 'pointer', minHeight: 36 };

const LinkButtons: React.FC<{ link: string; title: string; copied: string | null; onCopy: (l: string) => void; onShare: (l: string, t: string) => void }> = ({ link, title, copied, onCopy, onShare }) => (
  <div className="flex items-center" style={{ gap: 8, flex: 'none' }}>
    <button type="button" onClick={() => onCopy(link)} className="inline-flex items-center gap-1.5 font-semibold" style={pillYellow} aria-label={`Copy link to ${title}`}>
      {copied === link ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied === link ? 'Copied' : 'Copy link'}
    </button>
    <button type="button" onClick={() => onShare(link, title)} className="inline-flex items-center gap-1.5 font-semibold" style={pillLine} aria-label={`Share ${title}`}>
      <Share2 className="h-4 w-4" /> Share
    </button>
  </div>
);

export const ReferralShareCard: React.FC<{
  productId?: string | null;
  eventTitle?: string | null;
  showBalance?: boolean;
  style?: React.CSSProperties;
}> = ({ productId = null, eventTitle = null, showBalance = false, style }) => {
  const [me, setMe] = useState<AffiliateMe | null>(null);
  const [links, setLinks] = useState<ReferralLinks | null>(null);
  const { copied, copy, share } = useCopyShare();

  useEffect(() => {
    let alive = true;
    fetchAffiliateMe().then(d => { if (alive) setMe(d); }).catch(() => { if (alive) setMe(null); });
    if (!productId) fetchReferralLinks().then(d => { if (alive) setLinks(d); }).catch(() => { if (alive) setLinks(null); });
    return () => { alive = false; };
  }, [productId]);

  if (!me) return null;
  const balance = me.walletCents || 0;
  if (!me.isAffiliate && !(showBalance && balance > 0)) return null;
  const eventLink = productId && me.isAffiliate && me.code ? referralLinkFor(me.code, productId) : null;
  const rows = !productId && me.isAffiliate ? links : null;

  return (
    <section style={{ padding: '18px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', ...style }}>
      <div className="flex flex-wrap items-start justify-between" style={{ gap: 16 }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div className="mono-label" style={{ marginBottom: 6, color: 'var(--hj-beige-ink-2)' }}>
            {productId ? 'Know someone who’d want in?' : 'Refer a friend'}
          </div>
          <p className="m-0" style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
            {productId
              ? 'Send a friend your link to this event. When they buy a ticket through it, you earn Hey Joe credit on what they pay.'
              : 'Send a friend an event or the membership. When they buy a ticket or join Plus through your link, you earn Hey Joe credit on what they pay.'}
          </p>

          {eventLink && (
            <div className="flex items-center" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
              <code style={{ flex: '1 1 220px', padding: '9px 12px', borderRadius: 'var(--hj-r-sm)', background: 'rgba(255,255,255,0.55)', border: '1px solid var(--hj-beige-line)', fontSize: 12.5, color: 'var(--hj-beige-ink)', overflowWrap: 'anywhere' }}>
                {eventLink}
              </code>
              <LinkButtons link={eventLink} title={eventTitle ? `${eventTitle} on Hey Joe Plus` : 'Hey Joe Plus'} copied={copied} onCopy={copy} onShare={share} />
            </div>
          )}

          {rows && (
            <ul className="m-0 p-0" style={{ listStyle: 'none', marginTop: 12, display: 'grid', gap: 8 }}>
              {rows.events.map(e => (
                <li key={e.productId} className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'rgba(255,255,255,0.55)', border: '1px solid var(--hj-beige-line)' }}>
                  <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                    <Link to={`/course/${e.productId}`} style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-beige-ink)', textDecoration: 'none', overflowWrap: 'anywhere' }}>{e.title}</Link>
                    <div style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', marginTop: 2 }}>{whenLine(e.date)} · {priceLine(e)}</div>
                  </div>
                  <LinkButtons link={e.link} title={`${e.title} on Hey Joe Plus`} copied={copied} onCopy={copy} onShare={share} />
                </li>
              ))}
              {rows.membership && (
                <li className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'rgba(255,255,255,0.55)', border: '1px solid var(--hj-beige-line)' }}>
                  <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700 }}>{rows.membership.title}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', marginTop: 2 }}>For a friend who wants the whole thing.</div>
                  </div>
                  <LinkButtons link={rows.membership.link} title="Hey Joe Plus" copied={copied} onCopy={copy} onShare={share} />
                </li>
              )}
              {rows.events.length === 0 && (
                <li style={{ fontSize: 13, color: 'var(--hj-beige-ink-2)' }}>No paid events coming up right now. New ones show up here with your link.</li>
              )}
            </ul>
          )}
        </div>
        {showBalance && (
          <div style={{ flex: '0 0 auto', padding: '12px 16px', borderRadius: 'var(--hj-r-sm)', background: 'rgba(255,255,255,0.55)', border: '1px solid var(--hj-beige-line)', minWidth: 150, maxWidth: 220 }}>
            <div className="flex items-center gap-2 mono-label" style={{ color: 'var(--hj-beige-ink-2)', fontSize: 9 }}>
              <Wallet size={14} strokeWidth={1.8} /> Your Hey Joe credit
            </div>
            <div className="tabular" style={{ marginTop: 4, fontSize: 24, fontWeight: 800, fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-beige-ink)', lineHeight: 1.1 }}>
              ${(balance / 100).toFixed(2)}
            </div>
            <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--hj-beige-ink)' }}>Use it at checkout on events, classes and store items.</div>
          </div>
        )}
      </div>
      <p className="m-0" style={{ marginTop: 12, fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>
        <Link to="/affiliate" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>Referral earnings</Link>
      </p>
    </section>
  );
};

export default ReferralShareCard;
